<script setup>
import { ref, defineProps, defineModel } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { ru } from 'date-fns/locale';
import DropdownComponent from '@/components/UI/DropdownComponent.vue';

defineProps({
    title: {
        type: String,
        default: 'Параметры выгрузки',
    },
    idPrefix: {
        type: String,
        default: 'upload',
    },
});

const startDate = defineModel('startDate', {
    default: () => ({ type: 'exact', date: null }),
});
const endDate = defineModel('endDate', {
    default: () => ({ type: 'exact', date: null }),
});

const uploadOptions = ref({
    offsetOperation: [
        { name: 'Плюс', type: 'PLUS' },
        { name: 'Минус', type: 'MINUS' },
    ],
    offsetNames: [
        { name: 'Дней', type: 'DAY' },
        { name: 'Месяцев', type: 'MONTH' },
    ],
});

const startExactDate = ref({
    type: 'exact',
    date: null,
});

const startOffsetDate = ref({
    type: 'offset',
    length: 0,
    offsetOperation: uploadOptions.value.offsetOperation[0],
    offsetName: uploadOptions.value.offsetNames[0],
});

const endExactDate = ref({
    type: 'exact',
    date: null,
});

const endOffsetDate = ref({
    type: 'offset',
    length: 0,
    offsetOperation: uploadOptions.value.offsetOperation[0],
    offsetName: uploadOptions.value.offsetNames[0],
});

const handleInputStart = (event) => {
    const numericValue = event.target.value.replace(/[^\d]/g, '').slice(0, 4);
    startDate.value.length = numericValue === '' ? 0 : Number(numericValue);
};

const handleInputEnd = (event) => {
    const numericValue = event.target.value.replace(/[^\d]/g, '').slice(0, 4);
    endDate.value.length = numericValue === '' ? 0 : Number(numericValue);
};
</script>

<template>
    <div class="w-full mt-8 sm:mt-10">
        <div class="mb-3 sm:mb-4 font-bold text-base sm:text-lg">
            {{ title }}
        </div>
        <div class="flex flex-col lg:flex-row gap-8 lg:gap-12 xl:gap-16">
            <div class="flex-1 min-w-0">
                <div class="font-bold mb-4 sm:mb-5 text-base">Начало</div>
                <div class="flex flex-col">
                    <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:gap-10 mb-6 sm:mb-8 lg:mb-10">
                        <div class="flex items-center w-fit whitespace-nowrap">
                            <input
                                :id="`${idPrefix}-start-radio-1`"
                                type="radio"
                                :value="startExactDate"
                                :checked="startDate.type === startExactDate.type"
                                v-model="startDate"
                                class="w-4 h-4 cursor-pointer"
                            >
                            <label
                                :for="`${idPrefix}-start-radio-1`"
                                class="select-none ms-2 text-sm sm:text-base font-medium text-heading"
                            >
                                Точная дата
                            </label>
                        </div>
                        <VueDatePicker
                            v-show="startDate.type === 'exact'"
                            v-model="startDate.date"
                            :formats="{ input: 'dd.MM.yyyy' }"
                            :time-config="{ enableTimePicker: false }"
                            :locale="ru"
                            :disabled="startDate.type !== startExactDate.type"
                            class="w-full sm:w-36"
                            auto-apply
                        />
                    </div>

                    <div class="flex items-center mb-4">
                        <input
                            :id="`${idPrefix}-start-radio-2`"
                            type="radio"
                            :value="startOffsetDate"
                            :checked="startDate.type === startOffsetDate.type"
                            v-model="startDate"
                            class="w-4 h-4 cursor-pointer"
                        >
                        <label
                            :for="`${idPrefix}-start-radio-2`"
                            class="select-none ms-2 text-sm sm:text-base font-medium text-heading"
                        >
                            Смещение от текущей даты
                        </label>
                    </div>

                    <div
                        v-show="startDate.type === 'offset'"
                        class="flex flex-col sm:flex-row gap-2"
                    >
                        <DropdownComponent
                            v-model:value="startDate.offsetOperation"
                            :values="uploadOptions.offsetOperation"
                            label-key="name"
                        />
                        <input
                            v-model="startDate.length"
                            class="w-16 px-3 py-2 border rounded-lg text-sm text-right bg-white flex justify-between items-center"
                            placeholder="90"
                            @input="handleInputStart"
                        >
                        <DropdownComponent
                            v-model:value="startDate.offsetName"
                            :values="uploadOptions.offsetNames"
                            label-key="name"
                        />
                    </div>
                </div>
            </div>

            <div class="flex-1 min-w-0">
                <div class="font-bold mb-4 sm:mb-5 text-base">Конец</div>
                <div class="flex flex-col">
                    <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:gap-10 mb-6 sm:mb-8 lg:mb-10">
                        <div class="flex items-center w-fit whitespace-nowrap">
                            <input
                                :id="`${idPrefix}-end-radio-1`"
                                type="radio"
                                :value="endExactDate"
                                :checked="endDate.type === endExactDate.type"
                                v-model="endDate"
                                class="w-4 h-4 cursor-pointer"
                            >
                            <label
                                :for="`${idPrefix}-end-radio-1`"
                                class="select-none ms-2 text-sm sm:text-base font-medium text-heading"
                            >
                                Точная дата
                            </label>
                        </div>
                        <VueDatePicker
                            v-show="endDate.type === 'exact'"
                            v-model="endDate.date"
                            :formats="{ input: 'dd.MM.yyyy' }"
                            :time-config="{ enableTimePicker: false }"
                            :locale="ru"
                            :disabled="endDate.type !== endExactDate.type"
                            class="w-full sm:w-36"
                            auto-apply
                        />
                    </div>

                    <div class="flex items-center mb-4">
                        <input
                            :id="`${idPrefix}-end-radio-2`"
                            type="radio"
                            :value="endOffsetDate"
                            :checked="endDate.type === endOffsetDate.type"
                            v-model="endDate"
                            class="w-4 h-4 cursor-pointer"
                        >
                        <label
                            :for="`${idPrefix}-end-radio-2`"
                            class="select-none ms-2 text-sm sm:text-base font-medium text-heading"
                        >
                            Смещение от текущей даты
                        </label>
                    </div>

                    <div
                        v-show="endDate.type === 'offset'"
                        class="flex flex-col sm:flex-row gap-2"
                    >
                        <DropdownComponent
                            v-model:value="endDate.offsetOperation"
                            :values="uploadOptions.offsetOperation"
                            label-key="name"
                        />
                        <input
                            v-model="endDate.length"
                            class="w-16 px-3 py-2 border rounded-lg text-sm text-right bg-white flex justify-between items-center"
                            placeholder="90"
                            @input="handleInputEnd"
                        >
                        <DropdownComponent
                            v-model:value="endDate.offsetName"
                            :values="uploadOptions.offsetNames"
                            label-key="name"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
